import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Settings, Palette, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mainNavItems } from "@/lib/navigation-data";
import { useDebugControls } from "@/components/debug-controls-context";
import { useContactOverlay } from "@/components/contact-overlay-context";

type NavbarStyle = "glass" | "white" | "original";

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isPilatesPage = pathname === "/reformer-pilates";

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const { debugEnabled, showStudioMenu, setShowStudioMenu } = useDebugControls();
  const { openContact } = useContactOverlay();
  
  // Debug / Style Switcher State
  const [navbarStyle, setNavbarStyle] = useState<NavbarStyle>("glass");
  const [showDebug, setShowDebug] = useState(false);
  const [showColorDebug, setShowColorDebug] = useState(false);
  
  // Color State
  const [primaryHue, setPrimaryHue] = useState(187);
  const [primarySaturation, setPrimarySaturation] = useState(90);
  const [primaryLightness, setPrimaryLightness] = useState(62);

  const lastScrollY = useRef(0);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    setIsScrolled(lastScrollY.current > 50);
    setIsHidden(false);

    let frameId: number | null = null;

    const syncScrollState = () => {
      frameId = null;

      const currentScrollY = window.scrollY;
      const previousScrollY = lastScrollY.current;
      const nextScrolled = currentScrollY > 50;
      const nextHidden = currentScrollY > previousScrollY && currentScrollY > 200;

      setIsScrolled((previous) => (previous === nextScrolled ? previous : nextScrolled));
      setIsHidden((previous) => (previous === nextHidden ? previous : nextHidden));

      lastScrollY.current = currentScrollY;
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(syncScrollState);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!debugEnabled) {
      setShowDebug(false);
      setShowColorDebug(false);
    }
  }, [debugEnabled]);

  // Effect to update CSS variables for primary color
  useEffect(() => {
    const root = document.documentElement;
    let hslValue = `${primaryHue} ${primarySaturation}% ${primaryLightness}%`;
    
    // Override for Pilates page to use Gold and Black Background
    if (isPilatesPage) {
        // #C5A059 -> HSL(39, 48%, 56%)
        hslValue = "39 48% 56%";
        // Force black background for seamless footer
        root.style.setProperty("--background", "0 0% 0%");
    } else {
        // Reset background to default (let CSS handle it)
        root.style.removeProperty("--background");
    }

    root.style.setProperty("--primary", hslValue);
    root.style.setProperty("--accent", hslValue);
    root.style.setProperty("--ring", hslValue);
  }, [primaryHue, primarySaturation, primaryLightness, isPilatesPage]);

  // Hover effect class for nav items
  const navItemHoverClass = "relative after:content-[''] after:absolute after:w-0 after:h-[2px] after:bottom-0 after:left-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full hover:after:left-0";

  // Determine navbar classes based on style and scroll state
  const getNavbarClasses = () => {
    const baseClasses = "fixed top-0 left-0 right-0 z-40 transition-all duration-300 font-kanit border-b";
    
    if (navbarStyle === "glass") {
      return `${baseClasses} ${
        isScrolled 
          ? "bg-black/40 backdrop-blur-md border-white/10 shadow-lg" 
          : "bg-transparent border-transparent"
      }`;
    }
    
    if (navbarStyle === "white") {
      return `${baseClasses} ${
        isScrolled 
          ? "bg-white shadow-sm text-black border-black/5" 
          : "bg-white border-transparent text-black" // Pure white, no transparency/blur
      }`;
    }
    
    // Original style
    return `${baseClasses} ${
      isScrolled 
        ? "bg-background/95 backdrop-blur-md border-accent/10" 
        : "bg-background/80 backdrop-blur border-accent/10"
    }`;
  };

  // Text color adjustment for white navbar
  const getTextColorClass = () => {
    if (navbarStyle === "white") return "text-black hover:text-primary";
    return "text-foreground/80 hover:text-foreground";
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (!section) return false;

    const navbarOffset = 0;
    const targetTop = section.getBoundingClientRect().top + window.scrollY - navbarOffset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
    window.history.replaceState(null, "", `/#${sectionId}`);
    return true;
  };

  const handleHashNavClick = (href: string, closeMobileMenu = false) => {
    if (closeMobileMenu) setIsMenuOpen(false);

    const sectionId = href.slice(2);

    if (pathname !== "/") {
      navigate(href);
      return;
    }

    let attempts = 0;
    const maxAttempts = 30;

    const tryScroll = () => {
      if (scrollToSection(sectionId)) return;
      if (attempts < maxAttempts) {
        attempts += 1;
        window.setTimeout(tryScroll, 50);
      }
    };

    tryScroll();
  };

  return (
    <>
      {/* Debug Switcher - Hidden by default, toggleable via hidden trigger or just visible for dev */}
      {debugEnabled && (
        <div className="fixed top-24 right-4 z-[100] flex flex-col items-end gap-2">
          <div className="flex gap-2">
              <button 
                onClick={() => setShowColorDebug(!showColorDebug)}
                className="bg-black/50 p-2 rounded-full text-white hover:bg-primary hover:text-primary-foreground transition-colors"
                title="Change Primary Color"
              >
                <Palette className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setShowDebug(!showDebug)}
                className="bg-black/50 p-2 rounded-full text-white hover:bg-primary hover:text-primary-foreground transition-colors"
                title="Change Navbar Style"
              >
                <Settings className="w-5 h-5" />
              </button>
          </div>
          
          {showColorDebug && (
             <div className="bg-black/80 p-4 rounded-xl border border-white/10 backdrop-blur-md flex flex-col gap-4 w-56">
                <div>
                    <span className="text-xs font-bold text-white uppercase mb-1 block">Hue (Farbe)</span>
                    <input 
                        type="range" 
                        min="0" 
                        max="360" 
                        value={primaryHue} 
                        onChange={(e) => setPrimaryHue(Number(e.target.value))}
                        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <div className="text-xs text-right text-white/60 font-mono">{primaryHue}</div>
                </div>
                
                <div>
                    <span className="text-xs font-bold text-white uppercase mb-1 block">Saturation (Sättigung)</span>
                    <input 
                        type="range" 
                        min="0" 
                        max="100" 
                        value={primarySaturation} 
                        onChange={(e) => setPrimarySaturation(Number(e.target.value))}
                        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <div className="text-xs text-right text-white/60 font-mono">{primarySaturation}%</div>
                </div>

                <div>
                    <span className="text-xs font-bold text-white uppercase mb-1 block">Lightness (Helligkeit)</span>
                    <input 
                        type="range" 
                        min="0" 
                        max="100" 
                        value={primaryLightness} 
                        onChange={(e) => setPrimaryLightness(Number(e.target.value))}
                        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <div className="text-xs text-right text-white/60 font-mono">{primaryLightness}%</div>
                </div>
             </div>
          )}
          
          {showDebug && (
          <div className="bg-black/80 p-4 rounded-xl border border-white/10 backdrop-blur-md flex flex-col gap-2">
            <span className="text-xs font-bold text-white uppercase mb-1">Navbar Style</span>
            <button 
              onClick={() => setNavbarStyle("glass")}
              className={`px-3 py-1 rounded text-xs font-bold ${navbarStyle === "glass" ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}
            >
              Glass (Current)
            </button>
            <button 
              onClick={() => setNavbarStyle("white")}
              className={`px-3 py-1 rounded text-xs font-bold ${navbarStyle === "white" ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}
            >
              White
            </button>
            <button 
              onClick={() => setNavbarStyle("original")}
              className={`px-3 py-1 rounded text-xs font-bold ${navbarStyle === "original" ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}
            >
              Original
            </button>
            
            <div className="h-px bg-white/10 my-1" />
            
            <button 
              onClick={() => setShowStudioMenu(!showStudioMenu)}
              className={`px-3 py-1 rounded text-xs font-bold ${showStudioMenu ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}
            >
              Studio Menu: {showStudioMenu ? "ON" : "OFF"}
            </button>
          </div>
          )}
        </div>
      )}

      <motion.header
        variants={{
          visible: { y: 0 },
          hidden: { y: "-100%" },
        }}
        animate={isHidden ? "hidden" : "visible"}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={getNavbarClasses()}
      >
        {/* Schmale Streifen links/rechts, aber volle Breite nutzen */}
        <nav className="w-full flex h-20 items-center justify-between gap-4 page-content">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="/logo.webp"
              alt="Sepps Fitness"
              width={340}
              height={100}
              className="h-10 w-auto"
              style={isPilatesPage ? {
                  filter: "hue-rotate(-135deg) saturate(0.8) brightness(1.1)"
              } : undefined}
            />
          </Link>

          {/* Desktop Navigation - zentral, aber flexibel */}
          <div className="hidden md:flex flex-1 items-center justify-center">
            {/* Increased font weight to font-bold (was font-semibold) */}
            <div className="flex items-center gap-6 lg:gap-8 text-sm font-bold uppercase tracking-[0.18em]">

              {/* Studio Mega Menu */}
              {showStudioMenu && (
                  <div className="relative group">
                      <button className={`${getTextColorClass()} transition-colors py-2 font-kanit ${navItemHoverClass} flex items-center gap-1`}>
                          Studio
                          <ChevronDown className="w-3 h-3 opacity-50 group-hover:rotate-180 transition-transform duration-300" />
                      </button>
                      
                      {/* Dropdown */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 w-64">
                          <div className={`rounded-xl p-2 shadow-2xl border backdrop-blur-xl ${navbarStyle === 'white' ? 'bg-white border-black/5' : 'bg-black/90 border-white/10'}`}>
                              <div className="flex flex-col">
                                  <Link to="/yoga" className={`block px-4 py-3 rounded-lg transition-colors text-sm font-medium ${navbarStyle === 'white' ? 'hover:bg-gray-100 text-black' : 'hover:bg-white/10 text-white'}`}>
                                      Yoga
                                  </Link>
                                  <span className={`block px-4 py-3 rounded-lg text-sm font-medium opacity-40 cursor-not-allowed ${navbarStyle === 'white' ? 'text-black' : 'text-white'}`}>
                                      Ernährungsberatung
                                  </span>
                                  <span className={`block px-4 py-3 rounded-lg text-sm font-medium opacity-40 cursor-not-allowed ${navbarStyle === 'white' ? 'text-black' : 'text-white'}`}>
                                      Kinderbetreuung
                                  </span>
                                  <span className={`block px-4 py-3 rounded-lg text-sm font-medium opacity-40 cursor-not-allowed ${navbarStyle === 'white' ? 'text-black' : 'text-white'}`}>
                                      Personal Training
                                  </span>
                              </div>
                          </div>
                      </div>
                  </div>
              )}

              {mainNavItems.map((item) =>
                item.href.startsWith("/#") ? (
                  <button
                    key={item.href}
                    type="button"
                    onClick={() => handleHashNavClick(item.href)}
                    className={`${getTextColorClass()} transition-colors py-2 font-kanit ${navItemHoverClass} cursor-pointer uppercase`}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`${getTextColorClass()} transition-colors py-2 font-kanit ${navItemHoverClass}`}
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Right aligned CTA (desktop) */}
          <div className="hidden md:flex items-center shrink-0">
            <Button
              variant="outline"
              size="sm"
              // Increased border width to border-2 to prevent flickering
              className="rounded-full px-6 py-2 text-xs font-semibold tracking-[0.18em] uppercase font-kanit border-2 border-white/60 text-white hover:bg-primary hover:text-primary-foreground hover:border-primary"
              type="button"
              onClick={() => openContact({ category: "Mitgliedschaft" })}
            >
              Mitglied werden
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(true)}
            className={`md:hidden ml-auto transition-colors ${navbarStyle === "white" ? "text-black hover:text-primary" : "text-foreground hover:text-accent"}`}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </nav>
      </motion.header>

      {/* Full-Screen Overlay Menu (mobile) */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu Panel – slides in from right */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 34 }}
              className="fixed inset-y-0 right-0 z-50 w-full max-w-sm font-kanit overflow-y-auto border-l border-white/[0.08]"
              style={{
                background: "rgba(18,20,24,0.78)",
                backdropFilter: "blur(48px) saturate(180%)",
                WebkitBackdropFilter: "blur(48px) saturate(180%)",
                boxShadow: "inset 1px 0 0 rgba(255,255,255,0.04), -20px 0 60px rgba(0,0,0,0.5)",
              }}
            >
              {/* Header: Logo + Close */}
              <div className="flex items-center justify-between px-6 pt-6 pb-6 border-b border-white/[0.04]">
                <Link to="/" onClick={() => setIsMenuOpen(false)} className="shrink-0">
                  <img
                    src="/logo.webp"
                    alt="Sepps Fitness"
                    className="h-8 w-auto"
                  />
                </Link>
                <motion.button
                  onClick={() => setIsMenuOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-white/[0.08] text-white/50 hover:text-white hover:border-white/20 hover:bg-white/[0.04] transition-all"
                  aria-label="Close menu"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2, delay: 0.1 }}
                >
                  <X className="h-5 w-5" />
                </motion.button>
              </div>

              {/* Navigation */}
              <div className="flex flex-col px-6 pt-6 pb-6">
                {/* Studio Menu Mobile */}
                {showStudioMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.1 }}
                    className="mb-2"
                  >
                    <div className="text-[13px] font-bold text-primary uppercase tracking-[0.25em] mb-4">
                      Studio
                    </div>
                    <div className="flex flex-col gap-1 pl-4 border-l border-white/[0.06] mb-6">
                      <Link
                        to="/yoga"
                        onClick={() => setIsMenuOpen(false)}
                        className="py-2 text-lg font-medium text-white/80 hover:text-primary transition-colors tracking-wide"
                      >
                        Yoga
                      </Link>
                      <span className="py-2 text-lg font-medium text-white/20 tracking-wide">
                        Ernährungsberatung
                      </span>
                      <span className="py-2 text-lg font-medium text-white/20 tracking-wide">
                        Kinderbetreuung
                      </span>
                      <span className="py-2 text-lg font-medium text-white/20 tracking-wide">
                        Personal Training
                      </span>
                    </div>
                  </motion.div>
                )}

                {mainNavItems.map((item, index) => {
                  const delay = (showStudioMenu ? 1 : 0) * 0.08 + 0.12 + index * 0.06;
                  return (
                    <motion.div
                      key={item.href}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay }}
                    >
                      {item.href.startsWith("/#") ? (
                        <button
                          type="button"
                          onClick={() => handleHashNavClick(item.href, true)}
                          className="w-full text-left py-3 text-2xl font-semibold text-white hover:text-primary transition-colors tracking-[0.08em] uppercase cursor-pointer"
                        >
                          {item.label}
                        </button>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={() => setIsMenuOpen(false)}
                          className="block py-3 text-2xl font-semibold text-white hover:text-primary transition-colors tracking-[0.08em] uppercase"
                        >
                          {item.label}
                        </Link>
                      )}
                    </motion.div>
                  );
                })}

                {/* CTA + Instagram */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.12 + mainNavItems.length * 0.06 + 0.1 }}
                  className="pt-8 mt-4 border-t border-white/[0.04]"
                >
                  <div className="flex flex-col gap-3">
                    <Button
                      className="w-full rounded-full h-12 text-[11px] font-bold tracking-[0.2em] uppercase"
                      type="button"
                      onClick={() => {
                        setIsMenuOpen(false);
                        openContact({ category: "Mitgliedschaft" });
                      }}
                    >
                      Jetzt Mitglied werden
                    </Button>
                    <button
                      type="button"
                      className="w-full rounded-full h-12 text-[11px] font-bold tracking-[0.2em] uppercase border border-white/[0.12] text-white/70 hover:border-white/30 hover:text-white transition-all"
                      onClick={() => {
                        setIsMenuOpen(false);
                        openContact({ category: "Probetraining" });
                      }}
                    >
                      Personal Training buchen
                    </button>
                  </div>

                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/sepps.fitness/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2.5 mt-8 group"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-white/25 group-hover:text-primary transition-colors duration-300">
                      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
                    </svg>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/25 group-hover:text-primary transition-colors duration-300 font-medium">
                      @sepps.fitness
                    </span>
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
